import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { approvalInboxQuery } from "@/features/approvals/api/approvals.queries";
import { describeSubject } from "@/features/approvals/lib/subject-describers";
import { requesterNameOf } from "@/features/approvals/lib/requester-name";
import { SkLine } from "@altek/ui/components/feedback/Skeleton";
import { useDelayedLoading } from "@altek/ui/hooks/useDelayedLoading";
import { usePermissions } from "@/app/providers/AuthProvider";
import { decidesForOthers } from "@/lib/permissions/permissions";
import { eventListQuery } from "@/features/events/api/events.queries";
import { useScope } from "@/hooks/useScope";

/**
 * Üst çubuktaki onay düğmesinin altına açılan liste.
 *
 * NEDEN ÜST ÇUBUKTA. «Onay Merkezi» kenar çubuğundaki bir satırdı ve orada yanlış yerdeydi:
 * onay bir EKRAN değil, bir kuyruk — kişinin işini kesen, sayısı değişen bir şey. Zil için
 * verilen kararın aynısı: sayıyı gösteren yer, sayının götürdüğü yeri de göstermeli.
 *
 * BEŞ SATIR, HEPSİ DEĞİL — zil panelinin kuralının aynısı. Bu bir önizleme; tam liste
 * `/onaylar` değil, `/approvals`, ve bağlantısı altta.
 *
 * SAYI BURADA DEĞİL ÜST ÇUBUKTA HESAPLANIYOR (`AppLayout`), çünkü rozetin sayısı ile listenin
 * kendisi aynı sorgudan gelmeli: iki ayrı çağrı, rozette 3 yazıp listede 2 satır göstermenin
 * kısa yoluydu.
 */
export function ApprovalsPanel({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const permissions = usePermissions();
  const scope = useScope();
  // Kit simgeyi her hesapta çiziyor; onay anahtarı olmayan hesapta (taşımacının şoförü, 1 Kasım 2026)
  // kutu istenmiyor ve boş durumunu söylüyor — 403 yerine.
  const canReadApprovals = permissions.can("workflow.approval.read");
  // Üst çubuktaki sayaçla AYNI anahtar (`AppLayout`): en acil iş üstte (9 Ekim 2026).
  const inbox = useQuery({
    ...approvalInboxQuery({ page: 1, limit: 5, sortBy: "dueAt", sortOrder: "asc" }),
    enabled: canReadApprovals,
  });
  const rows = inbox.data?.rows ?? [];
  /* 200 ms koruması (31 Ekim 2026, İ-A9): hızlı inen kutuda iskelet bir kare yanıp sönüyordu. */
  const showSkeleton = useDelayedLoading(inbox.isLoading);

  /**
   * KONGRE ONAYI KUTUDA YOKTU (7 Ekim 2026, kullanılabilirlik turu). Kongre bir iş akışı
   * görevi değil, olayın kendisinde onaylanıyor; dört kongre «Planlandı»da onu beklerken kutu
   * yöneticiye «Onayınızı bekleyen iş yok» diyordu. Onaylayabilen kişiye sayısı ve listeye giden
   * bir satır — Panelim'deki «Onay bekleyen kongreler»in aynısı.
   */
  /**
   * Listeyi okumak `events.event.read` ister; onay anahtarı tek başına yetmez. Okuma kapısı
   * `const … = permissions.canForOthers("…")` biçiminde ve `enabled:` TEK SATIRDA: ekran
   * erişim çidi (`check:screen-access`) kapıyı ancak böyle tanıyor (9 Ekim 2026: çok satırlı
   * kapı çalışan, depo ve satış hesaplarında «eksik okuma» sayıldı).
   */
  const canReadEvents = permissions.canForOthers("events.event.read");
  /*
    ROZETLE AYNI KURAL (31 Ekim 2026, İ-A9): kutu kongreyi `canForOthers` ile, rozet ve sayfa
    `decidesForOthers` ile sayıyordu — yalnız yönetim rolünden gelen onay anahtarında kutu «3 kongre»
    derken rozet saymıyordu. Üçü artık aynı soruyu soruyor (`AppLayout`, `ApprovalInboxPage`).
  */
  const canApproveEvents =
    canReadEvents &&
    decidesForOthers(permissions, "events.event.approve") &&
    permissions.isModuleEnabled("module.events");
  const plannedEvents = useQuery({
    ...eventListQuery(scope.legalEntityId, { status: "PLANNED", limit: 1 }),
    enabled: canReadEvents && canApproveEvents && scope.legalEntityId !== null,
  });
  const eventTotal = canApproveEvents ? (plannedEvents.data?.pagination.total ?? 0) : 0;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
        <span className="text-sm font-semibold text-fg">Onay bekleyenler</span>
        {/*
          Yalnız kongre bekliyorken de (8 Ekim 2026): Onay Merkezi artık kongre sayısını da
          gösteriyor, yani bağlantı «bekleyen yok» diyen bir sayfaya gitmiyor.
        */}
        {rows.length > 0 || eventTotal > 0 ? (
          <Link
            to="/approvals"
            onClick={onNavigate}
            className="rounded-sm text-xs text-fg-muted underline underline-offset-2"
          >
            Tümünü gör
          </Link>
        ) : null}
      </div>

      {inbox.isLoading ? (
        showSkeleton ? (
          <div className="flex flex-col gap-2 p-3">
            <SkLine w="70%" />
            <SkLine w="100%" />
            <SkLine w="55%" />
          </div>
        ) : null
      ) : rows.length === 0 ? (
        eventTotal > 0 ? null : (
          <p className="px-3 py-6 text-center text-sm text-fg-muted">Onayınızı bekleyen iş yok.</p>
        )
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => {
            const subject = describeSubject(row.subjectType, row.context, row.subjectId);
            return (
              <li key={row.task.id} className="border-b border-border last:border-b-0">
                <Link
                  to={`/approvals?record=${encodeURIComponent(row.task.id)}`}
                  onClick={onNavigate}
                  className="flex flex-col gap-0.5 px-3 py-2.5"
                >
                  <span className="flex items-center gap-2">
                    {/*
                      Geciken işin işareti nokta — zil panelindeki okunmamış işaretinin eşi. DURUM
                      YALNIZ RENKLE DEĞİL (4. tur, 9 Ekim 2026): nokta süs (`aria-hidden`), satır
                      ekran okuyucuya «Gecikti:» diye başlıyor — zilin «Okunmamış:» kuralı.
                    */}
                    {row.overdue ? (
                      <>
                        <span
                          aria-hidden="true"
                          title="Gecikti"
                          className="size-1.5 shrink-0 rounded-full bg-danger"
                        />
                        <span className="sr-only">Gecikti: </span>
                      </>
                    ) : null}
                    <span className="truncate text-sm font-medium text-fg">{subject.title}</span>
                  </span>
                  <span className="truncate text-xs text-fg-muted">
                    {requesterNameOf(row.requester, row.context) ?? "Bilinmeyen talep sahibi"}
                    {subject.detail ? ` · ${subject.detail}` : ""}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {eventTotal > 0 ? (
        <Link
          to="/events?status=PLANNED"
          onClick={onNavigate}
          className="flex items-center justify-between gap-3 border-t border-border px-3 py-2.5"
        >
          <span className="text-sm font-medium text-fg">Onay bekleyen kongre</span>
          <span className="text-sm font-semibold tabular-nums text-warning-ink">{eventTotal}</span>
        </Link>
      ) : null}
    </div>
  );
}
